import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useState } from 'react';
import Button from 'components/Form/Button';
import { Icon, Icons } from 'components/Icon';
import { useGetTrusteeDetails } from 'common/queries-and-mutations/trustee';
import { useParams } from 'react-router-dom';
import { Loader } from 'components/Loader';
import { format } from 'date-fns';
import { BeneficiaryTypeEnum } from 'apps/user/types/user.enum.type';
import EditTrusteesModal from './EditTrusteesModal';
import BankAssets from './AssetCard/BankAssets';
import RSAAssets from './AssetCard/RSAssets';
import StocksAssets from './AssetCard/StocksAssets';
import BondsAssets from './AssetCard/BondsAssets';
import EmployeeAssets from './AssetCard/EmployeeAssets';
import PhysicalAsset from './AssetCard/PhysicalAssets';
import withCreatePortal from 'components/HOC/withCreatePortal';

const EnhancedEditTrusteesForm = withCreatePortal(EditTrusteesModal);

export default function TrusteesProfile() {
  const [showEditModal, setShowEditModal] = useState(false);
  const [userId, setUserId] = useState<number | null | undefined>(null);

  const { trusteeId } = useParams();

  const { data, isLoading, refetch } = useGetTrusteeDetails({
    trusteeId: Number(trusteeId)
  });

  return (
    <div>
      {isLoading && data ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <Breadcrumb title="Trustee Profile" />
          <div className="mt-1">
            <div className="mb-5 flex flex-row rounded bg-white">
              <div className="flex w-1/5 flex-col items-center justify-center border-r px-6">
                <div className="flex flex-col items-center justify-center gap-3">
                  <p className="text-lg font-bold text-[#353535]">
                    {`${data?.trusteeFirstName} ${data?.trusteeMiddleName} ${data?.trusteeLastName}`}
                  </p>
                </div>
              </div>
              <div className="w-full">
                <div className="border-b border-[#E5E5E5]">
                  <div className="mt-3 grid grid-cols-4 gap-x-16 px-6">
                    <div className="col-span-1 px-8 py-6">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Name
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {`${data?.trusteeFirstName ?? ''} ${
                          data?.trusteeMiddleName ?? ''
                        } ${data?.trusteeLastName ?? ''}`}
                      </p>
                    </div>
                    <div className="col-span-1 px-8 py-4">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Phone Number
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {data?.phoneNumber}
                      </p>
                    </div>
                    <div className="col-span-1 px-8 py-4">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Trustee Type
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {data?.trusteeType}
                      </p>
                    </div>
                    <div className="col-span-1 px-8 py-4">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Home Address
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {data?.address}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border-b border-[#E5E5E5]">
                  <div className="grid grid-cols-4 gap-x-16 px-6">
                    <div className="col-span-1 px-8 py-4">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Email address
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {data?.emailAddress}
                      </p>
                    </div>
                    {data?.trusteeType === BeneficiaryTypeEnum.CORPORATE && (
                      <div className="col-span-1 px-8 py-6">
                        <p className="text-[10px] font-normal text-[#7A7C87]">
                          Incorporation Date
                        </p>
                        <p className="mt-1 text-sm font-normal text-[#353535]">
                          {data && data.incorporationDate !== ''
                            ? format(
                                new Date(data.incorporationDate),
                                'dd MMMM yyyy'
                              )
                            : 'Unknown'}
                        </p>
                      </div>
                    )}
                    {data?.trusteeType === BeneficiaryTypeEnum.CORPORATE && (
                      <div className="col-span-1 px-8 py-6">
                        <p className="text-[10px] font-normal text-[#7A7C87]">
                          Incorporation Place
                        </p>
                        <p className="mt-1 text-sm font-normal text-[#353535]">
                          {data?.incorporationPlace || 'Unknown'}
                        </p>
                      </div>
                    )}
                    {data?.trusteeType === BeneficiaryTypeEnum.CORPORATE && (
                      <div className="col-span-1 px-8 py-6">
                        <p className="text-[10px] font-normal text-[#7A7C87]">
                          Rc Number
                        </p>
                        <p className="mt-1 text-sm font-normal text-[#353535]">
                          {data?.rcNumber || 'Unknown'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-x-16 px-6">
                  <div className="col-span-1 px-8 py-4">
                    <Button
                      variant="outline"
                      size="md"
                      className="w-[15rem] rounded border border-[#FF7A00] bg-[#FFF8F2] p-[0.1rem] text-[#FF7A00]"
                      label="Edit Trustee"
                      icon={<Icon name={Icons.Edit} />}
                      onClick={() => {
                        setUserId(data?.id);
                        setShowEditModal(true);
                      }}
                    ></Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="my-3">
              <p className="text-base text-[#7A7C87]">Assigned Trusts</p>
              {!data?.isAssigned ? (
                <div className="mt-10">
                  <p className="text-center font-circular text-lg font-bold">
                    No asset assigned
                  </p>
                </div>
              ) : (
                <div className="mt-2 h-[550px] space-y-3 overflow-auto scrollbar-hide">
                  {data?.banks.length > 0 && (
                    <BankAssets
                      banks={data?.banks}
                      trusteeId={Number(trusteeId)}
                      onSuccess={() => refetch()}
                    />
                  )}
                  {data?.pensions.length > 0 && (
                    <RSAAssets
                      assets={data.pensions}
                      trusteeId={Number(trusteeId)}
                      onSuccess={() => refetch()}
                    />
                  )}
                  {data?.stocks.length > 0 && (
                    <StocksAssets
                      assets={data.stocks}
                      trusteeId={Number(trusteeId)}
                      onSuccess={() => refetch()}
                    />
                  )}
                  {data?.bonds.length > 0 && (
                    <BondsAssets
                      assets={data.bonds}
                      trusteeId={Number(trusteeId)}
                      onSuccess={() => refetch()}
                    />
                  )}
                  {data?.employeeBenefits.length > 0 && (
                    <EmployeeAssets
                      assets={data.employeeBenefits}
                      trusteeId={Number(trusteeId)}
                      onSuccess={() => refetch()}
                    />
                  )}
                  {data?.physicalAssets.length > 0 && (
                    <PhysicalAsset
                      assets={data.physicalAssets}
                      trusteeId={Number(trusteeId)}
                      onSuccess={() => refetch()}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {showEditModal && userId && (
        <EnhancedEditTrusteesForm
          onClose={() => {
            setShowEditModal(false);
            refetch();
          }}
          onSuccess={() => {
            refetch();
            setShowEditModal(false);
            setUserId(null);
          }}
          trusteeId={userId}
        />
      )}
    </div>
  );
}
